import { useDarkMode } from '@/contexts/DarkModeContext';
import Alert from 'antd/es/alert';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import { styled } from 'styled-components';
import { JsonEditor } from 'ui-components';

import type { ConstantBlockConfig, PipelineBlock } from '@mai/types';

// Helper function to generate type descriptions (same as in ConstantNode)
const getTypeDescription = (value: unknown, maxLength: number = 50): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';

  const type = typeof value;

  if (type === 'string') return 'string';
  if (type === 'number') return 'number';
  if (type === 'boolean') return 'boolean';
  if (type === 'function') return 'function';
  if (type === 'symbol') return 'symbol';
  if (type === 'bigint') return 'bigint';

  if (Array.isArray(value)) {
    if (value.length === 0) return 'list()';

    // Get the type of the first element to infer array type
    const firstElementType = getTypeDescription(value[0], 20);
    return `list(${firstElementType})`;
  }

  if (type === 'object') {
    const keys = Object.keys(value as Record<string, unknown>);
    if (keys.length === 0) return 'object({})';

    // Build object type description
    const obj = value as Record<string, unknown>;
    const keyDescriptions: string[] = [];
    let currentLength = 8; // Start with 'object()'

    for (const key of keys) {
      const keyType = getTypeDescription(obj[key], 15);
      const keyDesc = `"${key}": ${keyType}`;

      // Check if adding this key would exceed max length
      if (currentLength + keyDesc.length + 2 > maxLength) {
        // +2 for ', '
        keyDescriptions.push('...');
        break;
      }

      keyDescriptions.push(keyDesc);
      currentLength += keyDesc.length + 2;
    }

    const content = keyDescriptions.join(', ');
    return `object({${content}})`;
  }

  return type;
};

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const TypePreview = styled.div`
  padding: ${({ theme }) => theme.paddingXS}px;
  background: ${({ theme }) => theme.colorBgContainer};
  border: 1px solid ${({ theme }) => theme.colorBorder};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  font-family: ${({ theme }) => theme.fontFamilyCode};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const EditorLabel = styled(Typography.Text)`
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.marginXXS}px;
`;

const EditorHelp = styled(Typography.Text)`
  color: ${({ theme }) => theme.colorTextSecondary};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  margin-bottom: ${({ theme }) => theme.marginXS}px;
`;

interface ConstantBlockEditorProps {
  block: PipelineBlock & { type: 'constant' };
  onConfigChange: (config: ConstantBlockConfig) => void;
}

export const ConstantBlockEditor: React.FC<ConstantBlockEditorProps> = ({
  block,
  onConfigChange,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isDarkMode] = useDarkMode();

  // Convert the current config_value to a string for the editor
  const currentValueString = React.useMemo(() => {
    const value = block.config.config_value;

    if (value === null) return 'null';
    if (value === undefined) return 'undefined';

    try {
      if (typeof value === 'string') {
        // If it's already a string, try to parse it to see if it's valid JSON
        try {
          JSON.parse(value);
          return value; // It's valid JSON string, keep as is
        } catch {
          return JSON.stringify(value); // It's a regular string, stringify it
        }
      } else {
        return JSON.stringify(value, null, 2);
      }
    } catch {
      return String(value);
    }
  }, [block.config.config_value]);

  // Get type description for preview
  const typeDescription = React.useMemo(() => {
    return getTypeDescription(block.config.config_value);
  }, [block.config.config_value]);

  const handleValueChange = React.useCallback(
    (value: string) => {
      if (isValid) {
        try {
          const parsedValue = JSON.parse(value);
          onConfigChange({
            ...block.config,
            config_value: parsedValue,
          });
        } catch {
          // If JSON parsing fails, treat as string
          onConfigChange({
            ...block.config,
            config_value: value,
          });
        }
      }
    },
    [block.config, onConfigChange, isValid],
  );

  const handleValidation = React.useCallback(
    (valid: boolean, errorMessage?: string) => {
      setIsValid(valid);
      setError(errorMessage || null);
    },
    [],
  );

  return (
    <EditorContainer>
      <EditorLabel>Constant Value</EditorLabel>
      <EditorHelp>
        Enter a constant value in JSON format. Supports strings, numbers,
        booleans, arrays, and objects.
      </EditorHelp>

      {error && (
        <Alert
          type="error"
          message="Invalid JSON"
          description={error}
          showIcon
        />
      )}

      <JsonEditor
        value={currentValueString}
        onChange={handleValueChange}
        onValidate={handleValidation}
        height="200px"
        placeholder='"Hello World"'
        theme={isDarkMode ? 'dark' : 'light'}
      />

      <TypePreview>
        <EditorLabel style={{ fontSize: '11px' }}>Inferred Type:</EditorLabel>{' '}
        {typeDescription}
      </TypePreview>
    </EditorContainer>
  );
};
